import {useCallback, useLayoutEffect, useRef, type RefObject} from 'react'

import {useResizeObserver} from '@github-ui/use-resize-observer'

export const TOP_OFFSET_CSS_VAR = '--sidebar-page-layout-top-offset'

/**
 * Keeps the `--sidebar-page-layout-top-offset` CSS variable in sync with the
 * layout's distance from the top of the viewport (or document when anchored)
 * so a `fitViewport` wrapper's
 * `min-height: calc(100vh - var(...))` fits the space remaining below any
 * chrome above it (global header, staffbar, notification banners, etc.) instead
 * of overflowing by that chrome's height.
 *
 * Rendered as a child of the layout root **only** when the layout opts into
 * `fitViewport`. Layouts that don't fit the viewport never mount this tracker,
 * so they never call `useResizeObserver`, attach a scroll listener, or run any
 * measurement — the opt-in has zero runtime cost. It renders no DOM (returns
 * `null`) and reads the layout root through the `rootRef` the parent owns, so
 * it never becomes the root's `firstElementChild` (which stays the PageLayout
 * wrapper that `measure` reads).
 *
 * The value is written imperatively — no React re-render — and only when it
 * actually changes, mirroring the `--app-offset` pattern in repo-agents and
 * `--artifacts-panel-top` in issue-viewer. The offset is re-read on scroll
 * (rAF-throttled, unless document-anchored) and on body reflow so it stays correct as chrome scrolls off
 * screen or resizes, and is cleared on unmount (when `fitViewport` toggles off
 * or the layout unmounts) so the CSS falls back to its `0px` default.
 */
export function LayoutTopOffsetTracker({
  rootRef,
  anchorToDocument = false,
}: {
  rootRef: RefObject<HTMLElement | null>
  anchorToDocument?: boolean
}): null {
  // Only write when the value changes to avoid style invalidation on every tick.
  const lastValueRef = useRef<number>(Number.NaN)

  const measure = useCallback(() => {
    const root = rootRef.current
    if (!root) return
    // The root <section> is `display: contents` (no box of its own), so measure
    // its first element child — the PageLayout wrapper — for the layout's
    // on-screen top. Document-anchored layouts must not grow as the page scrolls,
    // or they retain outer overflow after switching back from short-window scrolling.
    const box = root.firstElementChild
    const measuredTop = box?.getBoundingClientRect().top ?? 0
    // Rounding a document offset down leaves subpixel overflow and an inert scrollbar.
    const top = box ? Math.max(0, anchorToDocument ? measuredTop + window.scrollY : Math.round(measuredTop)) : 0
    if (top === lastValueRef.current) return
    lastValueRef.current = top
    root.style.setProperty(TOP_OFFSET_CSS_VAR, `${top}px`)
  }, [rootRef, anchorToDocument])

  // Observe the body so the offset recalculates when chrome above the layout
  // appears, disappears, or resizes (e.g. a dismissed sticky banner) or when the
  // viewport reflows — both change the body's size, and thus the layout's top,
  // which a ResizeObserver on the (position-shifting) layout root would miss.
  // The shared observer is rAF-coalesced, so the handler already runs off the
  // synchronous resize notification (no ResizeObserver loop).
  const observeBody = useResizeObserver(measure)

  useLayoutEffect(() => {
    // Measure once on mount so the offset is correct on first paint.
    lastValueRef.current = Number.NaN
    measure()

    // rAF-throttle scroll-driven remeasurement so we do at most one read per frame.
    let raf = 0
    const schedule = (event?: Event) => {
      const target = event?.target
      if (target instanceof Node && rootRef.current?.contains(target)) return
      if (raf) return
      raf = requestAnimationFrame(() => {
        raf = 0
        measure()
      })
    }
    // The layout's viewport-relative top changes as the page scrolls chrome off
    // (and back on) screen; neither ResizeObserver nor IntersectionObserver
    // reports that live position, so a capture-phase, rAF-throttled scroll
    // listener is required to keep the offset — and thus the fill height — in
    // sync (otherwise the wrapper keeps the initial, larger offset and leaves a
    // gap below the sidebar where it should expand). Body size changes are still
    // covered by the shared ResizeObserver.
    // eslint-disable-next-line github/prefer-observers
    if (!anchorToDocument) window.addEventListener('scroll', schedule, {capture: true})
    const stopObservingBody = observeBody(document.body)

    // Capture the node now: during unmount React may null the parent's ref
    // before this cleanup runs, so we can't rely on `rootRef.current` here.
    const root = rootRef.current
    return () => {
      window.removeEventListener('scroll', schedule, {capture: true})
      if (raf) cancelAnimationFrame(raf)
      stopObservingBody?.()
      root?.style.removeProperty(TOP_OFFSET_CSS_VAR)
    }
  }, [measure, observeBody, rootRef, anchorToDocument])

  return null
}
